import { createSlice } from "@reduxjs/toolkit";
import { clearChat, searchConversations, getChatList, deleteMediaChat, getChatMessageHistory, updateMediaProgress, uploadChatMedia, uploadChatDoc, getChatMedia, uploadChatVoiceNote, archiveConversation, unarchiveConversation, forwardChatMedia } from "./chatAction";


const addContentToExistingMessages = (incomingContent, existingArray) => {
    incomingContent.forEach((item) => {

        const existingIndex = existingArray.findIndex((existingItem) =>
            existingItem.messageId === item.messageId
        );

        if (existingIndex !== -1) {
            existingArray[existingIndex] = item;
        } else {
            existingArray.push(item);
        }
    });
};

const addOrUpdateConversations = (incomingConversations, existingArray) => {
    incomingConversations.forEach((incomingConversation) => {

        const existingIndex = existingArray.findIndex((existingConversation) =>
            existingConversation.room === incomingConversation.room
        );

        if (existingIndex !== -1) {
            // Update existing conversation
            existingArray[existingIndex] = incomingConversation;
        } else {
            // Add new conversation
            existingArray.push(incomingConversation);
        }
    });
};


const initialState = {
    isAtBottom: false,

    chatRoom: {},
    listLoading: false,
    chatMediaLoading: false,
    chatList: [],
    archivedChats: [],

    clearingChat: false,
    clearedStatus: 'idle',
    // searchedConversations: [], // Store all conversations here
    conversationSearchHistory: [], // Store the search history here
    searching: false, // Loading state for search
    searchError: null,

    messageLoading: false,
    messageError: null,
    error: null
}


const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        toggleIsAtBottom(state, action) {
            state.isAtBottom = action.payload
        },
        sendMessage(state, action) {
            const { roomId, message, listData, isSentByMe } = action.payload

            if (state.messageLoading) return

            if (!isSentByMe) {
                const listIndex = state.chatList.findIndex(
                    (item) => item.user.userId === listData.user.userId
                );

                if (listIndex !== -1) {
                    state.chatList[listIndex] = {
                        ...state.chatList[listIndex],
                        ...listData,
                        lastMessage: message
                    }
                } else {
                    const archivedIndex = state.archivedChats.findIndex(
                        (item) => item.user.userId === listData.user.userId
                    );
                    if (archivedIndex !== -1) {
                        state.archivedChats[archivedIndex] = {
                            ...state.archivedChats[archivedIndex],
                            ...listData,
                            lastMessage: message
                        }
                    } else {
                        state.chatList.unshift({
                            ...listData,
                            lastMessage: message
                        });
                    }
                }
            }


            if (state.chatRoom[roomId]) {
                const index = state.chatRoom[roomId].messages.findIndex(item => item.messageId === message.messageId)
                if (index !== -1) {
                    state.chatRoom[roomId].messages[index] = message
                } else {
                    state.chatRoom[roomId].messages.unshift(message)
                }

            } else {
                state.chatRoom[roomId] = { onlineStatus: '', typingStatus: '', roomId, messages: [message] }
            }
        },
        updateMessageInfo(state, action) {
            const { roomId, prevId, receiverId, room, data } = action.payload

            if (room) {
                const listIndex = state.chatList.findIndex(
                    (item) => item.user.userId === receiverId
                );
                if (listIndex !== -1) {
                    state.chatList[listIndex].room = room
                }
            }


            const index = state.chatRoom[roomId].messages.findIndex(chat => chat.messageId === prevId)
            if (index !== -1) {
                state.chatRoom[roomId].messages[index] = data
            }
        },
        reactToChat(state, action) {
            const { roomId, messageId, reaction } = action.payload
            const index = state.chatRoom[roomId]?.messages.findIndex(chat => chat.messageId === messageId)
            if (index !== 1) {
                state.chatRoom[roomId].messages[index].reaction = reaction
            }
        },
        markDelivered(state, action) {
            const { messageId, roomId } = action.payload

            const index = state.chatRoom[roomId].messages.findIndex(msg => msg.messageId === messageId)
            if (index !== -1) {

                state.chatRoom[roomId].messages[index].delivered = true
            } else {
                console.log('message not found; ')
            }
        },
        markAllAsRead(state, action) {
            const roomId = action.payload
            if (state.chatRoom[roomId] && state.chatRoom[roomId].messages) {
                const readMessages = state.chatRoom[roomId].messages.map(message => ({
                    ...message,
                    isReadStatus: true
                }));
                state.chatRoom[roomId] = {
                    ...state.chatRoom[roomId],
                    messages: readMessages
                };
            }
            const index = state.chatList.findIndex(list => list.id === roomId)
            if (index !== -1) {
                state.chatList[index].lastMessage.isReadStatus = true
            }
        },
        markAsRead(state, action) {
            const { roomId, messageId, chatUserId } = action.payload
            if (state.chatRoom[roomId] && state.chatRoom[roomId].messages) {
                const index = state.chatRoom[roomId].messages.findIndex(message => message.messageId === messageId);
                if (index !== -1) {
                    state.chatRoom[roomId].messages[index].read = true
                }
            }
            state.chatList = state.chatList.map(chat => {
                if (chat.user.userId === chatUserId) {
                    return {
                        ...chat,
                        unreadCount: 0
                    }
                }
                return chat
            })
        },
        updateScrollTo(state, action) {
            const { roomId, messageId, scrolledTo } = action.payload
            const index = state.chatRoom[roomId]?.messages.findIndex(chat => chat.messageId === messageId)
            const chat = {
                ...state.chatRoom[roomId].messages[index],
                scrolledTo: scrolledTo
                // ...(scrolledTo ? { scrolledTo: scrolledTo } : {})
            }
            state.chatRoom[roomId].messages[index] = chat
        },
        updateOnlineStatus(state, action) {
            const { status, roomId } = action.payload
            if (state.chatRoom[roomId]) {
                state.chatRoom[roomId].onlineStatus = status
            }
        },
        updateTypingStatus(state, action) {
            const { status, userId, roomId } = action.payload
            if (state.chatRoom[roomId]) {
                state.chatRoom[roomId].typingStatus = status
            }

            const index = state.chatList.findIndex(list => list.user.userId === userId)
            if (index !== -1) {
                state.chatList[index] = { ...state.chatList[index], typing: status }
            }
        },
        deleteMessage(state, action) {
            const { roomId, messageId } = action.payload
            const newMessages = state.chatRoom[roomId].messages.filter(message => message.messageId !== messageId)
            state.chatRoom[roomId].messages = newMessages

        },
        resetClearStatus(state, action) {
            state.clearedStatus = 'idle'
        },
        setConversationSearch(state, action) {
            addOrUpdateConversations(action.payload, state.conversationSearchHistory)
        },
        updateBlockStatus(state, action) {
            const { userId, youBlockedThem } = action.payload;
            const index = state.chatList.findIndex(item => item.user.userId === userId);
            if (index !== -1) {
                state.chatList[index] = { ...state.chatList[index], youBlockedThem };
            }
        }

    },

    extraReducers: (builder) => {

        builder
            .addCase(getChatList.pending, (state) => {
                state.listLoading = true
            })
            .addCase(getChatList.fulfilled, (state, action) => {
                state.listLoading = false
                const { conversations } = action.payload
                const unArchivedChat = conversations.filter(list => !list.archivedStatus)
                const archivedChat = conversations.filter(list => list.archivedStatus)

                state.chatList = unArchivedChat
                state.archivedChats = archivedChat

            })
            .addCase(getChatList.rejected, (state, action) => {
                state.listLoading = false
                state.error = action.error.message
            })
        builder
            .addCase(getChatMessageHistory.pending, (state) => {
                state.messageLoading = true
                state.error = null
            })
            .addCase(getChatMessageHistory.fulfilled, (state, action) => {
                state.messageLoading = false
                const { messages, myId, userId, lastEvaluatedKey } = action.payload
                const roomId = `${userId}_${myId}`
                if (state.chatRoom[roomId]) {
                    // state.chatRoom[roomId] = { roomId, messages: [...state.chatRoom[roomId].messages, ...messages], lastEvaluatedKey }
                    addContentToExistingMessages(messages, state.chatRoom[roomId].messages)
                    state.chatRoom[roomId].lastEvaluatedKey = lastEvaluatedKey
                } else {
                    state.chatRoom[roomId] = { onlineStatus: '', typingStatus: '', roomId, messages: messages, lastEvaluatedKey }
                }
            })
            .addCase(getChatMessageHistory.rejected, (state, action) => {
                state.messageLoading = false
                state.messageError = 'failed'
            })

        builder
            .addCase(getChatMedia.pending, (state, action) => {
                state.chatMediaLoading = true
            })
            .addCase(getChatMedia.fulfilled, (state, action) => {
                state.chatMediaLoading = false
                const { roomId, medias } = action.payload

                state.chatRoom[roomId].medias = medias
            })
            .addCase(getChatMedia.rejected, (state) => {
                state.chatMediaLoading = false
            })

        builder
            .addCase(searchConversations.pending, (state) => {
                state.searching = true; // Set searching to true while the API call is in progress
                state.searchError = null; // Reset any previous error
            })
            .addCase(searchConversations.fulfilled, (state, action) => {
                state.searching = false; // Set searching to false when the call is successful
                addOrUpdateConversations(action.payload, state.conversationSearchHistory); // Update conversation search history
            })
            .addCase(searchConversations.rejected, (state, action) => {
                state.searching = false; // Set searching to false when there's an error
                state.searchError = action.payload || action.error.message; // Capture the error message
            });

        builder
            .addCase(updateMediaProgress, (state, action) => {
                const { fakeId, roomId, progress } = action.payload
                const index = state.chatRoom[roomId].messages.findIndex(message => message.messageId === fakeId)
                if (index !== -1) {
                    // console.log('message found')
                    state.chatRoom[roomId].messages[index].progress = progress
                }
            })

            .addCase(uploadChatVoiceNote.fulfilled, (state, action) => {
                const { fakeId, mediaUrl, messageId, roomId, createdAt, conversationId, fileType } = action.payload
                const index = state.chatRoom[roomId].messages.findIndex(message => message.messageId === fakeId)
                if (index !== -1) {
                    state.chatRoom[roomId].messages[index] = { ...state.chatRoom[roomId].messages[index], messageId: messageId, createdAt, sent: true, conversationId, mediaUrl: { fileType, postUrl: mediaUrl } }
                } else {

                }
            })
            .addCase(uploadChatMedia.fulfilled, (state, action) => {
                const { fakeId, mediaUrl, messageId, roomId, createdAt, conversationId, fileType } = action.payload
                const index = state.chatRoom[roomId].messages.findIndex(message => message.messageId === fakeId)
                if (index !== -1) {
                    console.log('message found')
                    state.chatRoom[roomId].messages[index] = { ...state.chatRoom[roomId].messages[index], messageId: messageId, createdAt, sent: true, conversationId, mediaUrl: { fileType, postUrl: mediaUrl } }
                } else {

                }
            })

            .addCase(uploadChatDoc.fulfilled, (state, action) => {
                const { fakeId, mediaUrl, messageId, roomId, createdAt, conversationId, fileType, imageName } = action.payload
                // console.log('log fake id in the reducer: ', fakeId)
                const index = state.chatRoom[roomId].messages.findIndex(message => message.messageId === fakeId)
                if (index !== -1) {
                    console.log('message found')
                    state.chatRoom[roomId].messages[index] = { ...state.chatRoom[roomId].messages[index], messageId: messageId, createdAt, sent: true, conversationId, mediaUrl: { fileType, postUrl: mediaUrl, imageName: imageName } }
                } else {

                }
            })

            .addCase(forwardChatMedia.fulfilled, (state, action) => {
                const { messageId, roomId, mediaUrl, createdAt, conversationId, tempId } = action.payload
                const index = state.chatRoom[roomId].messages.findIndex(message => message.messageId === tempId)
                if (index !== -1) {
                    state.chatRoom[roomId].messages[index] = { ...state.chatRoom[roomId].messages[index], messageId: messageId, createdAt, sent: true, conversationId, mediaUrl: { ...state.chatRoom[roomId].messages[index].mediaUrl, postUrl: mediaUrl } }
                }
            })


            .addCase(deleteMediaChat.fulfilled, (state, action) => {
                const { messageId, roomId } = action.payload
                const newMessages = state.chatRoom[roomId].messages.filter(message => message.messageId !== messageId)
                state.chatRoom[roomId].messages = newMessages
            })

        builder

            .addCase(archiveConversation.fulfilled, (state, action) => {
                const { chatItem } = action.payload;
                const chatIndex = state.chatList.findIndex(chat => chat.room === chatItem.room);

                if (chatIndex !== -1) {
                    // Update the archivedStatus immutably
                    state.chatList[chatIndex] = {
                        ...state.chatList[chatIndex], // Spread the existing chat object
                        archivedStatus: true // Update the archivedStatus
                    };
                    // Add the updated chat item to the archivedChats array
                    state.archivedChats.push({
                        ...state.chatList[chatIndex] // Push the updated chat item
                    });

                    // Remove the archived chat from the chatList
                    state.chatList = state.chatList.filter(
                        conversation => conversation.room !== chatItem.room
                    );
                }
            })

            .addCase(archiveConversation.rejected, (state, action) => {
                state.error = action.payload || 'Failed to archive conversation';
            })

            .addCase(unarchiveConversation.fulfilled, (state, action) => {
                const { roomId } = action.payload;

                // Find the index of the conversation in the archivedChats array
                const archivedIndex = state.archivedChats.findIndex(chat => chat.room === roomId);

                if (archivedIndex !== -1) {
                    // Update the archivedStatus immutably
                    const unarchivedChat = {
                        ...state.archivedChats[archivedIndex],
                        archivedStatus: false
                    };

                    // Push the updated chat back to chatList
                    state.chatList.push(unarchivedChat);

                    // Remove the conversation from archivedChats array
                    state.archivedChats = state.archivedChats.filter(
                        conversation => conversation.room !== roomId
                    );
                }
            })

        builder

            .addCase(clearChat.pending, (state) => {
                state.clearingChat = true
            })
            .addCase(clearChat.fulfilled, (state, action) => {
                state.clearingChat = false
                const { conversationId, roomId } = action.payload

                console.log('clearedChat payload: ', action.payload)

                const chatIndex = state.chatList.findIndex(chat => chat.room === conversationId)

                state.chatRoom[roomId].messages = []
                if (chatIndex !== -1) {
                    state.chatList[chatIndex] = {
                        ...state.chatList[chatIndex],
                        lastMessage: {}
                    }
                }
                state.clearedStatus = 'success'
            })

            .addCase(clearChat.rejected, (state, action) => {
                state.clearingChat = false

                state.error = action.error.message
            })
    }
})

export const { toggleIsAtBottom, sendMessage, markAllAsRead, markAsRead, updateMessageInfo, updateScrollTo, reactToChat, markDelivered, updateOnlineStatus, updateTypingStatus, deleteMessage, resetClearStatus, setConversationSearch, updateBlockStatus } = chatSlice.actions
export default chatSlice.reducer;